import { useState, useEffect, useMemo } from "react"
import dayjs from "dayjs"
import Header from "components/Layouts/Header"
import { useAppSelector } from "app/hooks"
import { useLogWater } from "../hooks/useLogWater"
import { useBodyDisplayUnits } from "features/profile/hooks/body"
import {
  getWaterPresets,
  getWaterUnit,
  getWaterVolumeInMl,
  WATER_PRESETS_ML,
  getMaxWaterIntake,
} from "utils/waterVolumeConverter"
import WaterItem from "./WaterItem"

import {
  WaterWrapper,
  WaterLogContent,
  AddConsumptionCard,
  AddConsumptionTitle,
  WaterItemsContainer,
} from "./styled"
import AppStyleCtaBox from "components/Common/AppStyleCtaBox"
import Button from "components/Buttons/Button"
import { useSetPageTitle } from "libs/ga"
import { useBackToHome } from "features/meal/hooks"
import WaterInput from "components/inputs/WaterInput"

const Water = () => {
  useSetPageTitle("Water")

  const backToHome = useBackToHome()

  const { bodyDisplayUnits } = useBodyDisplayUnits()

  // Get dynamic presets based on unit
  const waterPresets = useMemo(
    () =>
      bodyDisplayUnits ? getWaterPresets(bodyDisplayUnits) : WATER_PRESETS_ML,
    [bodyDisplayUnits],
  )

  const unit = bodyDisplayUnits ? getWaterUnit(bodyDisplayUnits) : "ml"

  // Initialize local amounts based on presets
  const [localAmounts, setLocalAmounts] = useState<Record<number, number>>(() =>
    Object.fromEntries(waterPresets.map((amount) => [amount, 0])),
  )

  // Track manually entered custom total (null means use preset-calculated total)
  const [customTotal, setCustomTotal] = useState<number | null>(null)

  // Reset localAmounts when presets change (unit changes)
  useEffect(() => {
    setLocalAmounts(
      Object.fromEntries(waterPresets.map((amount) => [amount, 0])),
    )
  }, [bodyDisplayUnits])

  const selectedDate = useAppSelector((state) => state.home.selectedDate)
  const { logWaterLoading, handleLogWater } = useLogWater(backToHome)

  const localTotal = waterPresets.reduce(
    (sum, amount) => sum + amount * (localAmounts[amount] || 0),
    0,
  )

  // Use custom total if manually entered, otherwise use preset-calculated total
  const displayTotal = customTotal !== null ? customTotal : localTotal

  const maxWater = getMaxWaterIntake(unit)

  const isLogButtonDisabled = displayTotal === 0 || logWaterLoading

  const handleManualInput = (value: number) => {
    // Reset all presets to 0
    setLocalAmounts(
      Object.fromEntries(waterPresets.map((amount) => [amount, 0])),
    )
    // Set custom total
    setCustomTotal(value)
  }

  const handleIncrement = (amount: number) => {
    setCustomTotal(null) // Clear custom total when using presets
    setLocalAmounts((prev) => ({
      ...prev,
      [amount]: (prev[amount] || 0) + 1,
    }))
  }

  const handleDecrement = (amount: number) => {
    setCustomTotal(null) // Clear custom total when using presets
    setLocalAmounts((prev) => ({
      ...prev,
      [amount]: Math.max(0, (prev[amount] || 0) - 1),
    }))
  }

  const onLogWater = () => {
    if (displayTotal === 0 || !bodyDisplayUnits) return

    const date = selectedDate
      ? dayjs(selectedDate).format("YYYY-MM-DD")
      : dayjs().format("YYYY-MM-DD")

    // Convert to ml for server
    const amountInMl = getWaterVolumeInMl(bodyDisplayUnits, displayTotal)
    handleLogWater({ amount: amountInMl, date })
  }

  const onBack = () => {
    if (!logWaterLoading) {
      backToHome()
    }
  }

  return (
    <WaterWrapper>
      <Header title="Water Tracker" onBack={onBack} isAppPaddingStyle />
      <WaterLogContent>
        <WaterInput
          value={displayTotal}
          onChange={handleManualInput}
          label="Total Intake"
          disabled={logWaterLoading}
          unit={unit}
        />

        <AddConsumptionCard>
          <AddConsumptionTitle>Add consumption</AddConsumptionTitle>
          <WaterItemsContainer>
            {waterPresets.map((amount) => (
              <WaterItem
                key={amount}
                amount={amount}
                unit={unit}
                count={localAmounts[amount] || 0}
                plusDisabled={displayTotal + amount > maxWater}
                onIncrement={() => handleIncrement(amount)}
                onDecrement={() => handleDecrement(amount)}
              />
            ))}
          </WaterItemsContainer>
        </AddConsumptionCard>
      </WaterLogContent>
      <AppStyleCtaBox>
        <Button
          style={{
            width: "100%",
          }}
          $type="primary"
          disabled={isLogButtonDisabled}
          onClick={onLogWater}
        >
          Log water
        </Button>
      </AppStyleCtaBox>
    </WaterWrapper>
  )
}

export default Water
